import {
    ITransaction,
    TransactionType,
} from "../../Admin/Components/User/List/Utils";
import { DatatableFilter } from "../../Models/DatatableFilter";
import Transaction, { OTransactionCount } from "../../Models/Transaction";
import axios from "../AxiosInstance";
import UtilsService from "../UtilsService";

const getAmount = (
    description: string,
    isUsdcc = false,
): number | undefined => {
    const regex = isUsdcc ? /contre\s+(.+?)\s+USDCC/ : /contre\s+(.+?)\s+USDT/;
    const match = description.match(regex);

    if (match && match[1]) {
        return +match[1].replace(" ", "");
    } else {
        return undefined;
    }
};

const getFee = (description: string, isUsdcc = false): number | undefined => {
    const regex = isUsdcc ? /(\d+)\s*USDCC de frais/ : /(\d+)\s*USDT de frais/;
    const match = description.match(regex);

    if (match && match[1]) {
        return +match[1];
    } else {
        return undefined;
    }
};

const map = (item: any): Transaction => {
    const transaction = new Transaction(
        item.id,
        item.description,
        item.amount,
        item.status,
        item.created_at,
        item.user,
    );

    // transaction.description = item.description;
    if (transaction.isClishaNode) {
        const amount = getAmount(
            item.description,
            item.description.includes("USDCC"),
        );
        const fee = getFee(
            item.description,
            item.description.includes("USDCC"),
        );
        transaction.amount = amount ?? transaction.amount;
        transaction.fee = fee ?? transaction.fee;
    } else if (transaction.isStopedClisha) {
        transaction.amount = 0;
        transaction.fee = 0;
    } else {
        transaction.amount = item.amount;
        transaction.fee = item.fee;
    }
    transaction.status = item.status;
    transaction.type = item.type;
    transaction.user_wallet = item.user_wallet;
    transaction.date = UtilsService.getBeautifulFormatedDateWithTime(
        item.created_at,
    );
    transaction.date_fr = UtilsService.getDateFormat_D_M_YY(item.created_at);
    transaction.user = item.user;

    transaction.target = item.target;
    transaction.usd_price = item.usd_price;

    return transaction;
};

const getUserTransactions = async (): Promise<Array<Transaction>> => {
    return axios
        .get(`/wallet-history/get-user-wallet-history`)
        .then(({ data }) => {
            const assets: Transaction[] = data.map((item: any) => map(item));

            return assets;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getUserTransactionsPaginate = async (
    filter: DatatableFilter,
    order_by_date?: string,
): Promise<any> => {
    return axios
        .get(`/wallet-history/get-user-wallet-all-history`, {
            params: { ...filter, order_by_date },
        })
        .then(({ data }) => {
            const assets: Transaction[] = data.data.map((item: any) =>
                map(item),
            );

            return {
                data: assets,
                count: data.count,
                currentPage: data.currentPage,
                nextPage: data.nextPage,
                prevPage: data.prevPage,
                lastPage: data.lastPage,
            };
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getUserTransactionsPaginateAdmin = async (
    filter: DatatableFilter,
    uid: string,
    order_by_date?: string,
): Promise<any> => {
    return axios
        .get(`/wallet-history/get-user-wallet-all-history-admin/${uid}`, {
            params: { ...filter, order_by_date },
        })
        .then(({ data }) => {
            const assets: Transaction[] = data.data.map((item: any) =>
                map(item),
            );

            return {
                data: assets,
                count: data.count,
                currentPage: data.currentPage,
                nextPage: data.nextPage,
                prevPage: data.prevPage,
                lastPage: data.lastPage,
            };
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getAllTransactions = async (): Promise<Array<Transaction>> => {
    return axios
        .get(`/wallet-history/get-all-wallet-history`)
        .then(({ data }) => {
            const assets: Transaction[] = data.map((item: any) => map(item));

            return assets;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getAllTransactionsPaginate = async (
    filter: DatatableFilter,
): Promise<any> => {
    return axios
        .get(`/wallet-history/get-all-wallet-history`, { params: filter })
        .then(({ data }) => {
            const assets: Transaction[] = data.data.map((item: any) =>
                map(item),
            );

            return {
                data: assets,
                count: data.count,
                currentPage: data.currentPage,
                nextPage: data.nextPage,
                prevPage: data.prevPage,
                lastPage: data.lastPage,
            };
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getAllTransactionsCount = async (): Promise<any> => {
    return axios
        .get(`/wallet-history/transaction-count`)
        .then(({ data }) => data as OTransactionCount)
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getTotalCommission = async (): Promise<number> => {
    return axios
        .get(`/wallet-history/get-user-total-commission`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getCurrentMonthCommission = async (): Promise<number> => {
    return axios
        .get(`/wallet-history/get-user-current-month-total-commission`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const creditOrDebit = async (
    payload: ITransaction,
    type: TransactionType,
    uid?: string,
) => {
    try {
        const result = await axios.post(
            `/user-wallet/${
                type == TransactionType.CREDIT ? "credit" : "debit"
            }/${uid}`,
            {
                ...payload,
            },
        );

        return Promise.resolve(result.data.message);
    } catch (err: any) {
        const error =
            err.response && err.response.data && err.response.data.message
                ? err.response.data.message
                : err.message;

        return Promise.reject(error);
    }
};

const getTransactionsByUser = async (
    uid?: string,
): Promise<Array<Transaction>> => {
    return axios
        .get(`/wallet-history/get-user-wallet-history-by-ui`, {
            params: { uid },
        })
        .then(({ data }) => {
            const assets: Transaction[] = data.map((item: any) => map(item));

            return assets;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getUserCommission = async (): Promise<Array<Transaction>> => {
    return axios
        .get(`/wallet-history/get-commission`)
        .then(({ data }) => {
            const assets: Transaction[] = data.map((item: any) => map(item));

            return assets;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getGeneralReport = async (): Promise<any> => {
    return axios
        .get(`/wallet-history/general-report`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};
const getGeneralReportBonusTotal = async (): Promise<number> => {
    return axios
        .get(`/wallet-history/bonus-total`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const monthlyTotalCBUSDAmount = async (): Promise<any> => {
    return axios
        .get(`/wallet-history/cb`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};
const lastMonthTotalCBUSDAmount = async (): Promise<number> => {
    return axios
        .get(`/wallet-history/last-month-cb`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getGeneralReportTo2Dates = async (
    startDate: Date,
    endDate: Date,
): Promise<any> => {
    return axios
        .get(`/wallet-history/general-report-between-dates`, {
            params: { startDate: startDate, endDate: endDate },
        })
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const formatType = (type: string) => {
    let result = "";
    switch (type) {
        case "deposit":
            result = "Dépôt";
            break;
        case "unstaking":
            result = "Dépôt";
            break;
        case "transferin":
            result = "Dépôt";
            break;
        case "commission":
            result = "Dépôt";
            break;
        case "daily_roi":
            result = "Dépôt";
            break;
        case "daily_knt":
            result = "Dépôt";
            break;
        case "deposit_visa":
            result = "Dépôt";
            break;
        case "deposit_kalypay":
            result = "Dépôt";
            break;
        case "deposit_crypto":
            result = "Dépôt";
            break;
        case "rank_bonus":
            result = "Dépôt";
            break;
        case "pool_bonus":
            result = "Dépôt";
            break;
        case "withdraw":
            result = "Retrait";
            break;
        case "withdraw_crypto":
            result = "Retrait";
            break;
        case "withdraw_kalypay":
            result = "Retrait";
            break;
        case "transferout":
            result = "Retrait";
            break;
        case "order_packbooster":
            result = "Retrait";
            break;
        case "staking":
            result = "Retrait";
            break;
        case "swapout":
            result = "Échange";
            break;
        default:
            break;
    }

    return result;
};

const updateSeen = async (): Promise<any> => {
    return axios.put(`/wallet-history/seen-transaction`).catch((err: any) => {
        return Promise.reject(UtilsService.getAxiosErrorMessage(err));
    });
};

const getUserTransactionsWithoutPaginate = async (
    filter: DatatableFilter,
): Promise<any> => {
    return axios
        .get(`/wallet-history/get-user-wallet-history-export`, {
            params: { ...filter },
        })
        .then(({ data }) => {
            const transactions: Transaction[] = data.map((item: any) =>
                map(item),
            );
            return transactions;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getLatestTransactionsExpenses = async (): Promise<any> => {
    return axios
        .get(`/wallet-history/get-last-expenses`)
        .then(({ data }) => {
            const transactions: Transaction[] = data.map((item: any) =>
                map(item),
            );
            return transactions;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getLatestTransactionsEarnings = async (): Promise<any> => {
    return axios
        .get(`/wallet-history/get-last-earnings`)
        .then(({ data }) => {
            const transactions: Transaction[] = data.map((item: any) =>
                map(item),
            );
            return transactions;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const checkTransaction = async (wallet_history_id: number): Promise<any> => {
    return axios
        .put(`/wallet-history/check/${wallet_history_id}`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};
const getTotalAgentDepoistWithdraw = async (uid: string): Promise<any> => {
    return axios
        .get(`/wallet-history/get-total-agent-depoist-withdraw/${uid}`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const cancelTransaction = async (
    wallet_history_id: number,
    token2FA: string,
): Promise<any> => {
    return axios
        .put(`/wallet-history/cancel/${wallet_history_id}`, { token2FA })
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const sendTransaction = async (
    wallet_history_id: number,
    token2FA: string,
): Promise<any> => {
    return axios
        .put(`/wallet-history/send/${wallet_history_id}`, { token2FA })
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const TransactionService = {
    getUserTransactions,
    getTotalCommission,
    getCurrentMonthCommission,
    credit: creditOrDebit,
    getTransactionsByUser,
    getAllTransactions,
    getUserCommission,
    getUserTransactionsPaginate,
    getAllTransactionsPaginate,
    getAllTransactionsCount,
    getGeneralReport,
    getGeneralReportTo2Dates,
    formatType,
    monthlyTotalCBUSDAmount,
    lastMonthTotalCBUSDAmount,
    getUserTransactionsPaginateAdmin,
    getGeneralReportBonusTotal,
    updateSeen,
    getUserTransactionsWithoutPaginate,
    getLatestTransactionsExpenses,
    getLatestTransactionsEarnings,
    checkTransaction,
    getTotalAgentDepoistWithdraw,
    cancelTransaction,
    sendTransaction,
};

export default TransactionService;
